import { Code, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { useVatRate } from '../../vat-rates/hooks/useVatRate';
import { useUpsertPriceForm } from '../hooks/useUpsertPriceForm';
import type { UpsertBranchStockPriceDto } from '../types/BranchStockDto';
import { UpsertPriceForm } from './UpsertPriceForm';

interface EditBranchStockPriceModalProps {
	close: () => void;
	isOpened: boolean;
	updateBranchStockTable: () => void;
	skuid: string;
	vatRateId?: string;
}

export function EditAllBranchStockModalPrices({
	isOpened,
	close,
	updateBranchStockTable,
	skuid,
	vatRateId,
}: EditBranchStockPriceModalProps) {
	const { organisation } = useParams();

	const successModal = useModal();

	const vatRate = useVatRate(vatRateId);

	const { form, inputs } = useUpsertPriceForm(undefined, vatRate);

	const editBranchStockPriceMutation = mutate<
		UpsertBranchStockPriceDto,
		number
	>(`${organisation}/branch-stock/edit-price/all/${skuid}`, inputs);

	const resetAndClose = () => {
		form.reset();
		successModal.close();

		close();
	};

	const complete = () => {
		if (editBranchStockPriceMutation.data) {
			form.setInitialValues(inputs);
			updateBranchStockTable();
		}

		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={
					<>
						<Title label={'Edit All Branch Prices'} />
						<Code>
							Prices will only be updated on existing branch stocks. 
						</Code>
					</>
				}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={editBranchStockPriceMutation.isLoading} />
				<SmallModal
					isOpen={successModal.isOpen}
					close={complete}
					title={'Branch Price Edited!'}
					body={`Prices have been successfully updated for ${editBranchStockPriceMutation.data?.value} branch stocks. Click Ok to continue...`}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editBranchStockPriceMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<UpsertPriceForm form={form} />
					<FormButtons close={resetAndClose} reducePaddingTop />
				</form>
			</Modal>
		</>
	);
}
